import React, { useState, useEffect, useRef } from "react";
import { Layout, Button, List, Avatar, Drawer } from "antd";
import { MessageOutlined, MenuOutlined } from "@ant-design/icons";
import { io } from "socket.io-client";
import { getApi } from "../../../services/api";
import { BACKEND_BASE_URL } from "../../../utils/constants";

const socket = io(BACKEND_BASE_URL, { transports: ["websocket"] });

socket.on("connect_error", (err) => {
	console.error("Connection error:", err);
});
const { Header, Content, Sider } = Layout;
const ADMIN_GROUP = "admin_group";

const MessageListPage = () => {
	const [selectedUserId, setSelectedUserId] = useState(1);
	const [newMessage, setNewMessage] = useState("");
	const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
	const [siderVisible, setSiderVisible] = useState(false);
	const [conversations, setConversations] = useState({});
	// const [replyMessage, setReplyMessage] = useState("");
	const [userIdToName, setUserIdToName] = useState({});
	const messagesEndRef = useRef(null);

	const scrollToBottom = () => {
		if (messagesEndRef.current) {
			messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
		}
	};

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth <= 768);
		};
		socket.emit("joinAdminRoom");
		// Join admin room on load
		// Listen for new messages from users
		socket.on("userMessage", ({ message, sender }) => {
			console.log(message);
			setConversations((prevConversations) => ({
				...prevConversations,
				[sender.id]: [
					...(prevConversations[sender.id] || []),
					{ recipient: ADMIN_GROUP, message, timestamp: Date.now() },
				],
			}));
			if (!userIdToName[sender.id])
				setUserIdToName((prev) => ({
					...prev,
					[sender.id]: sender.name,
				}));
			scrollToBottom();
		});

		window.addEventListener("resize", handleResize);

		const fetchPreviousMessages = async () => {
			try {
				const messages = await getApi("/messages");
				if (!messages) return;
				const adminMessages = messages.filter(
					(message) => message?._id === ADMIN_GROUP
				)[0];

				messages.forEach((message) => {
					if (message?._id === ADMIN_GROUP) return;
					setUserIdToName((prev) => ({
						...prev,
						[message?._id]: message?.senderName,
					}));

					const received =
						(adminMessages &&
							adminMessages?.messages.filter(
								(msg) => msg?.recipient?.id === message?._id
							)) ||
						[];
					const allMessages = [
						...message?.messages,
						...received,
					].sort(
						(a, b) =>
							new Date(a?.timestamp) - new Date(b?.timestamp)
					);
					console.log(
						"All Messages of ",
						message?.senderName,
						message?.messages,
						allMessages
					);
					setConversations((prev) => ({
						...prev,
						[message?._id]: allMessages,
					}));
				});
			} catch (error) {
				console.error(error);
			}
		};
		fetchPreviousMessages();

		return () => {
			socket.off("userMessage");
			socket.off("connect");
			window.removeEventListener("resize", handleResize);
		};
	}, []);
	useEffect(() => {
		scrollToBottom();
	}, [conversations, selectedUserId]);
	const handleSendMessage = () => {
		if (!newMessage.trim() || !selectedUserId) return;
		socket.emit("sendMessageToUser", {
			userId: selectedUserId,
			message: newMessage.trim(),
			recipientName: userIdToName[selectedUserId],
		});
		setConversations((prevConversations) => ({
			...prevConversations,
			[selectedUserId]: [
				...(prevConversations[selectedUserId] || []),
				{
					recipient: {
						id: selectedUserId,
						name: userIdToName[selectedUserId],
					},
					message: newMessage.trim(),
					timestamp: Date.now(),
				},
			],
		}));
		setNewMessage("");
	};
	const handleKeyDown = (event) => {
		if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
			event.preventDefault(); // Prevent newline
			handleSendMessage(); // Call the form submit logic
		}
	};

	const handleReplyClick = (userId) => {
		setSelectedUserId(userId);
		if (isMobile) {
			setSiderVisible(false);
		}
		scrollToBottom();
	};

	const toggleSider = () => {
		setSiderVisible(!siderVisible);
	};

	const renderSider = () => (
		<div className="h-full overflow-y-auto">
			<Header className="bg-white shadow flex justify-center items-center">
				<h1 className="text-xl font-bold text-mainColor text-center">
					Chat history
				</h1>
			</Header>
			<List
				itemLayout="horizontal"
				dataSource={Object.entries(userIdToName)}
				renderItem={([userId, userName]) => (
					<List.Item
						onClick={() => handleReplyClick(userId)}
						style={{
							cursor: "pointer",
							padding: "1rem",
							backgroundColor:
								selectedUserId === userId ? "#dcdcdc" : "white",
							// borderRadius: "1rem"
						}}
					>
						<List.Item.Meta
							avatar={<Avatar src="/images/avatar.png" />}
							title={userName}
							description={
								conversations[userId]?.slice(-1)[0]?.message
							}
						/>
						<Button
							type="primary"
							icon={<MessageOutlined />}
							size="small"
							onClick={(e) => {
								e.stopPropagation();
								handleReplyClick(userId);
							}}
						>
							Reply
						</Button>
					</List.Item>
				)}
			/>
		</div>
	);

	return (
		<Layout className="h-full p-0">
			{!isMobile && (
				<Sider width={300} className="bg-white overflow-y-auto">
					{renderSider()}
				</Sider>
			)}
			<Layout>
				<Header className="bg-white shadow flex items-center">
					{isMobile && (
						<Button
							icon={<MenuOutlined />}
							onClick={toggleSider}
							className="mr-4"
						/>
					)}
					<h1 className="text-xl">Messages</h1>
				</Header>
				<Content className="p-4">
					<div className="border rounded-lg p-4 bg-white shadow h-full overflow-y-auto flex flex-col">
						<h2 className="text-lg font-semibold">
							Chat with {userIdToName[selectedUserId]}
						</h2>
						<div className="mt-4 flex-grow overflow-y-auto">
							{conversations[selectedUserId]?.map(
								(message, idx) => {
									const isOwnMessage =
										message?.recipient?.id ===
										selectedUserId;
									return (
										<div
											key={idx}
											className={`flex items-start mb-2 ${
												!isOwnMessage
													? ""
													: "justify-end"
											}`}
										>
											{!isOwnMessage && (
												<Avatar
													src="/images/avatar.png"
													className="mr-2"
												/>
											)}
											<div
												className={`p-2 rounded-lg ${
													!isOwnMessage
														? "bg-gray-200 text-black"
														: "bg-blue-500 text-white"
												}`}
											>
												{message?.message}
											</div>
											{isOwnMessage && (
												<Avatar
													src="/images/avatar.png"
													className="ml-2"
												/>
											)}
										</div>
									);
								}
							)}
							<div ref={messagesEndRef} />
						</div>
						<div className="mt-4">
							<textarea
								className="w-full border rounded p-2"
								placeholder="Type your message..."
								rows="3"
								value={newMessage}
								onChange={(e) => setNewMessage(e.target.value)}
								onKeyDown={handleKeyDown}
							></textarea>
							<Button
								type="primary"
								className="mt-2"
								onClick={handleSendMessage}
							>
								Send
							</Button>
						</div>
					</div>
				</Content>
			</Layout>
			{isMobile && (
				<Drawer
					placement="left"
					closable={false}
					onClose={toggleSider}
					visible={siderVisible}
					width={300}
				>
					{renderSider()}
				</Drawer>
			)}
		</Layout>
	);
};

export default MessageListPage;
